import {formatDate, formatPercent} from '@angular/common';
import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {combineLatest, of, Subscription} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {AdminDashboardReportBlock, AdminDashboardReportProvider, AdminDashboardReportRow} from '@features/admin-panel/models/admin-dashboard-report.models';
import {AdminDashboardReportRegistry} from '@features/admin-panel/services/admin-dashboard-report-registry.service';
import {
  AdminDashboardQueryParams,
  AdminDashboardScope,
  AdminDashboardTrialEnding,
  AdminDashboardTrialLikelihood,
  AdminDashboardTrialLikelihoods,
  AdminDashboardWidgetState
} from '@features/admin-panel/models/admin-dashboard.models';

/** A trial ending this week with its predicted conversion, when the trial likelihood query has one (ITP-E-152-02). */
export interface AdminDashboardTrialEndingView extends AdminDashboardTrialEnding {
  likelihood: AdminDashboardTrialLikelihood | null;
}

/**
 * Joins each trial to its prediction: the same company (case-insensitive) and the same product (trimmed,
 * case-insensitive). A prediction without a product name matches any trial of its company. No match → null.
 * The first prediction for a key wins.
 */
export function joinTrialLikelihoods(rows: AdminDashboardTrialEnding[],
                                     items: AdminDashboardTrialLikelihood[] | null | undefined): AdminDashboardTrialEndingView[] {
  const norm = (value: string | null | undefined): string => (value ?? '').trim().toLowerCase();
  const byProduct = new Map<string, AdminDashboardTrialLikelihood>();
  const byCompany = new Map<string, AdminDashboardTrialLikelihood>();
  for (const item of items ?? []) {
    const company = norm(item?.companyId);
    if (!company) {
      continue;
    }
    const product = norm(item.productName);
    if (product) {
      const key = `${company}|${product}`;
      if (!byProduct.has(key)) {
        byProduct.set(key, item);
      }
    } else if (!byCompany.has(company)) {
      byCompany.set(company, item);
    }
  }
  return rows.map(row => ({
    ...row,
    likelihood: byProduct.get(`${norm(row.companyId)}|${norm(row.productName)}`) ?? byCompany.get(norm(row.companyId)) ?? null
  }));
}

/** The card's title and description, on screen and in the PDF report (OCA-E-159 DetailSections:298). */
const TITLE = 'Trials ending this week';
const DESCRIPTION = 'Running trials that end in the next 7 days, with the likelihood they convert.';

/**
 * Trials ending this week: the in-scope companies' live TaxMart trials whose end falls in the next 7 days, soonest
 * first, as a list — company, product and the prediction's reason, the conversion rate with its confidence, and when
 * the trial ends. The prediction comes from AdminDashboardTrialLikelihoodQuery when there is one. Both queries need
 * APBilling, the feature the page shows this card for. Reloads whenever the dashboard's query params change.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-trials-ending',
  templateUrl: './admin-dashboard-trials-ending.component.html',
  styleUrls: ['./admin-dashboard-trials-ending.component.scss']
})
export class AdminDashboardTrialsEndingComponent implements OnChanges, OnInit, OnDestroy, AdminDashboardReportProvider {
  @Input() scope: AdminDashboardScope = 'Clients';
  @Input() params: AdminDashboardQueryParams;
  /** What the card has to show, reported to the dashboard (ITP-E-173-01). */
  @Output() stateChange = new EventEmitter<AdminDashboardWidgetState>();
  widgetState: AdminDashboardWidgetState = 'loading';

  readonly title = TITLE;
  readonly description = DESCRIPTION;
  readonly failedTitle = 'Couldn\'t load trials ending this week';
  rows: AdminDashboardTrialEndingView[] = [];
  loaded = false;
  /** The trials query failed: the card says so instead of looking empty. */
  failed = false;

  private loadSubscription: Subscription;

  constructor(private dataService: AdminDashboardDataService, private reportRegistry: AdminDashboardReportRegistry, private host: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['params']) {
      this.load();
    }
  }

  ngOnInit(): void {
    this.reportRegistry.register('licences-revenue', this.host.nativeElement, this);
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.reportRegistry.unregister(this);
  }

  /** "ends today", "ends in 1 day", "ends in 6 days" (OCA-E-159 DetailSections:315). */
  endsText(daysLeft: number): string {
    return daysLeft <= 0 ? 'ends today' : `ends in ${countOf(daysLeft, 'day')}`;
  }

  /** The secondary line: the product, then the prediction's reason when there is one. */
  detailText(row: AdminDashboardTrialEndingView): string {
    return row.likelihood?.reason ? `${row.productName} · ${row.likelihood.reason}` : row.productName;
  }

  /** "72% · high"; "—" without a prediction. */
  likelihoodText(row: AdminDashboardTrialEndingView): string {
    return row.likelihood
      ? `${formatPercent(row.likelihood.rate ?? 0, 'en-US', '1.0-0')} · ${row.likelihood.confidence}`
      : '—';
  }

  /** The card for the PDF report, built from what it shows now; never reloads (ITP-E-146-10). */
  reportBlocks(): AdminDashboardReportBlock[] {
    if (!this.loaded) {
      return [];
    }
    const title = TITLE;
    if (this.failed) {
      return [{kind: 'empty', title, text: this.failedTitle}];
    }
    const subtitle = DESCRIPTION;
    if (!this.rows.length) {
      return [{kind: 'empty', title, subtitle, text: 'No trials end this week'}];
    }
    return [{
      kind: 'table',
      title,
      subtitle,
      columns: [
        {header: 'Company', weight: 2}, {header: 'Product', weight: 2}, {header: 'Ends'},
        {header: 'Days left', align: 'right'}, {header: 'Likelihood', align: 'right'}
      ],
      // The urgency stripe: red at 1 day or less, amber above.
      rows: this.rows.map((row): AdminDashboardReportRow => ({
        cells: [row.companyName, row.productName, formatDate(row.trialEnd, 'd MMM', 'en-US', 'UTC'), String(row.daysLeft), this.likelihoodText(row)],
        tone: row.daysLeft <= 1 ? 'red' : 'amber'
      }))
    }];
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    if (!this.params) {
      return;
    }
    // A reload keeps showing the last result until the new one arrives, so only the first load reports 'loading'.
    if (!this.loaded) {
      this.report('loading');
    }
    this.loadSubscription = combineLatest([
      this.dataService.getData<AdminDashboardTrialEnding[]>('AdminDashboardTrialsEndingQuery', this.params, 'Rows'),
      // Without predictions the trials are still listed, just without a likelihood.
      this.dataService.getTrialLikelihoods(this.params).pipe(
        catchError(() => of(null as AdminDashboardTrialLikelihoods))
      )
    ]).subscribe({
      next: ([rows, likelihoods]) => this.show(joinTrialLikelihoods(Array.isArray(rows) ? rows : [], likelihoods?.Items), false),
      error: () => this.show([], true)
    });
  }

  private show(rows: AdminDashboardTrialEndingView[], failed: boolean): void {
    this.rows = rows;
    this.failed = failed;
    this.loaded = true;
    this.report(failed ? 'failed' : rows.length ? 'content' : 'empty');
  }

  private report(state: AdminDashboardWidgetState): void {
    this.widgetState = state;
    this.stateChange.emit(state);
  }
}
